/**
 * Semble MDT — Clinical Interview report staleness helpers
 * --------------------------------------------------------
 * Two Semble MDT Report paragraphs are summarised from APPROVED Clinical
 * Interview report paragraphs (not raw proforma inputs):
 *
 *   - semble_clinical_history ← the CI narrative from Presenting Complaint
 *     through Psychiatric History (SEMBLE_CLINICAL_HISTORY_SOURCES)
 *   - semble_risk_assessment  ← the CI Risk Assessment paragraph
 *     (SEMBLE_RISK_ASSESSMENT_SOURCES)
 *
 * If a clinician goes back to the CI review page (RD_CI.js) and edits &
 * re-approves any of those source paragraphs AFTER the Semble report was
 * generated, the corresponding Semble paragraph is now stale.  This module
 * detects that by comparing the current CI outputs against the exact input
 * values the Semble Cloud Function stored when it last generated each summary.
 *
 * The check runs on CI review page EXIT (not on each individual edit/approval)
 * so a clinician can edit several paragraphs and only regenerate once — see
 * RD_CI.js handleExitReview.
 *
 * These source lists MUST stay in sync with the Python CF's
 * CLINICAL_HISTORY_SOURCES / RISK_ASSESSMENT_SOURCES
 * (python-functions/single_report/semble_mdt_summaries.py) so the frontend
 * dirty-check matches what the CF actually fed the LLM.  Only the code (first
 * tuple element) is used for the comparison; the label is informational.
 */

/** Semble paragraph codes stored under manageReports.sections.10. */
export const SEMBLE_CLINICAL_HISTORY_CODE = 'semble_clinical_history';
export const SEMBLE_RISK_ASSESSMENT_CODE = 'semble_risk_assessment';

// Mirrors RISK_ASSESSMENT_SOURCES in semble_mdt_summaries.py.
export const SEMBLE_RISK_ASSESSMENT_SOURCES = [
  ['ci_RSK_summary', 'Risk Assessment'],
];

// Mirrors CLINICAL_HISTORY_SOURCES in semble_mdt_summaries.py: the full CI
// narrative from Presenting Complaint through Psychiatric History, EXCLUDING
// physical health (ci_MED_health_conditions), medications (ci_MED_medications),
// Risk (ci_RSK_summary) and MSE (ci_MSE_summary) — those either feed the
// dedicated Physical Health / Risk Semble paragraphs or are out of range.
export const SEMBLE_CLINICAL_HISTORY_SOURCES = [
  ['ci_COM_referral_information', 'Referral information'],
  ['ci_COM_history_of_presenting_complaint', 'History of presenting complaint'],
  ['ci_COM_impact_on_daily_functioning', 'Impact on daily functioning'],
  ['ci_DEV_pre_natal_and_birth', 'Pre-natal and birth'],
  ['ci_DEV_early_years', 'Early years'],
  ['ci_DEV_childhood_and_adolescence', 'Childhood and adolescence'],
  ['ci_DEV_childhood_living_environment', 'Childhood living environment'],
  ['ci_DEV_education', 'Education'],
  ['ci_DEV_current_living_situation', 'Current living situation'],
  ['ci_DEV_relationships', 'Relationships'],
  ['ci_DEV_employment', 'Employment'],
  ['ci_LIF_lifestyle_factors', 'Lifestyle factors'],
  ['ci_LIF_alcohol_and_substance_use', 'Alcohol and substance use'],
  ['ci_LIF_habits', 'Habits'],
  ['ci_LIF_driving', 'Driving'],
  ['ci_FOR_history_of_legal_issues', 'History of legal issues'],
  ['ci_FOR_perpetrator_history', 'History as a perpetrator'],
  ['ci_FOR_victim_history', 'History as a victim'],
  ['ci_FAM_health_conditions', 'Family history'],
  ['ci_MED_hospital_visits', 'Hospital visits'],
  ['ci_MED_allergies', 'Allergies'],
  ['ci_MED_vision_and_hearing', 'Vision and hearing'],
  ['ci_MED_womens_health', "Women's health"],
  ['ci_PSY_previous_and_current_diagnoses', 'Psychiatric history — diagnoses'],
  ['ci_PSY_previous_and_current_treatments', 'Psychiatric history — treatments'],
  ['ci_PSY_trauma_and_risk_history', 'Psychiatric history — trauma and risk history'],
];

// Each Semble paragraph paired with the CI source codes that feed it.
const SEMBLE_CI_PARAGRAPHS = [
  [SEMBLE_RISK_ASSESSMENT_CODE, SEMBLE_RISK_ASSESSMENT_SOURCES],
  [SEMBLE_CLINICAL_HISTORY_CODE, SEMBLE_CLINICAL_HISTORY_SOURCES],
];

/**
 * Extract the CI-output values the Semble CF stored as generation input for a
 * given Semble paragraph, keyed by CI code.  The CF stores these under the
 * paragraph's "Clinician Notes" input category as { value, source } entries.
 */
export function getStoredSembleInputValues(sembleInputs, sembleCode) {
  const stored = sembleInputs?.[sembleCode]?.['Clinician Notes'];
  if (!stored || typeof stored !== 'object') return {};
  const values = {};
  for (const [key, entry] of Object.entries(stored)) {
    if (entry?.value) values[key] = String(entry.value).trim();
  }
  return values;
}

/**
 * Compare the current CI outputs against the stored generation inputs for the
 * given source list.  Dirty when any source paragraph's current text differs
 * from what was fed to the LLM (covers edits, and sections becoming
 * populated/emptied since generation).
 */
export function isSembleSourceDirty(sources, sembleInputs, sembleCode, currentOutputs) {
  const storedValues = getStoredSembleInputValues(sembleInputs, sembleCode);
  const outputs = currentOutputs || {};
  return sources.some(([code]) => {
    const current = (outputs[code] || '').trim();
    const stored = (storedValues[code] || '').trim();
    return current !== stored;
  });
}

/**
 * Return the Semble paragraph codes that are stale relative to the current CI
 * outputs, suitable for passing straight to TriggerSembleMdtReport's
 * forceRegenerateKeys.  Returns [] when nothing changed.
 *
 * @param {object} manageReports - dataModel.6_manageReports
 * @param {object} currentOutputs - the live CI outputs
 *   (manageReports.sections.1.outputs), keyed by CI code.
 */
export function getDirtySembleCiParagraphs(manageReports, currentOutputs) {
  const sembleInputs = manageReports?.sections?.['10']?.inputs || {};
  return SEMBLE_CI_PARAGRAPHS
    .filter(([sembleCode, sources]) => (
      isSembleSourceDirty(sources, sembleInputs, sembleCode, currentOutputs)
    ))
    .map(([sembleCode]) => sembleCode);
}
